import type { AnomalyChoice, DestinationChoice } from '@ghp/domain'

/**
 * **Les cinq colonnes du constructeur d'anomalie quand le référentiel n'est pas descendu** —
 * recopiées du prototype validé (`mocks/ghp-bo/editor.js`, `EDA_ORIGINS`, `EDA_LOCAL`,
 * `EDA_EVAC`, `EDA_DESTS`, `EDA_TECH` ; `mocks/ghp-ct/screens.js` porte les mêmes listes sous le
 * préfixe `ANOM_`).
 *
 * ⚠️ **Un repli, pas le référentiel.** Le vocabulaire descendu (M17, M18) prime dès qu'une liste
 * en porte au moins une entrée active : ce sont ces valeurs-là que l'administration gère, et
 * elles emportent avec elles les **codes** sans lesquels une anomalie n'est plus rattachable au
 * référentiel. Ces libellés-ci n'en ont pas.
 *
 * ⚠️ **Elles ne vivent pas dans `@ghp/domain`**, et c'est la règle de M17 : « les listes sont du
 * code, les lignes sont des données ». Figer 52 lignes dans le domaine en ferait un second
 * référentiel, concurrent de celui que le bureau administre.
 *
 * Ce qu'elles achètent : l'écran n'est **jamais nu**. Une tablette qui n'a pas encore synchronisé
 * laisse relever une anomalie — sans code, avec sa phrase, qui est l'archive (M19, M21).
 */
export const FALLBACK_ORIGINS: readonly AnomalyChoice[] = [
  'Réseau inaccessible',
  'Cuisine',
  'WC',
  'Urinoir',
  'Salle de bain',
  'Lave-linge',
  'Lavabo',
  'Lave-mains',
  'Bonde de sol',
  '2 Bondes de sol',
  'Baignoire',
  'Douche',
  'Évier',
  'Robinet extérieur',
  'Fontaine extérieure',
].map((label) => ({ label }))

/**
 * La colonne « Provenance » — **l'étage** depuis le 21/09/2026 (M34), là où le prototype listait
 * le local d'où part l'écoulement (« du garage », « de l'annexe »). Ce que le local disait
 * d'utile — dans quel bâtiment de la parcelle — est passé sur `SubmittedAnomaly.building`, une
 * énumération figée (`anomalyBuildingSchema`), et ces quatre valeurs sont désactivées au
 * référentiel (`seed/reference.sql`, `migrations/0030`).
 *
 * ⚠️ **Le repli suit le référentiel, sinon il le contredit.** Une tablette qui n'a pas encore
 * synchronisé proposerait les anciens locaux, l'agent en cocherait un, et le bureau relirait une
 * provenance que plus aucune ligne active ne porte. « Canalisation cassée » reste : c'est
 * l'intrus que le prototype range là, et aucune autre colonne ne le porte.
 *
 * C'est la **seule** provenance que l'agent saisit depuis #114 : les pastilles d'étage numéroté
 * qui alimentaient `SubmittedAnomaly.location` ont été retirées. L'immeuble alimente `.building`.
 */
export const FALLBACK_PROVENANCES: readonly AnomalyChoice[] = [
  'du rez-de-chaussée',
  "de l'étage",
  'du sous-sol',
  'Canalisation cassée',
].map((label) => ({ label }))

export const FALLBACK_EVACUATIONS: readonly AnomalyChoice[] = [
  'E Mén via Descente EP',
  'EU via Descente EP',
  "Pas d'eau",
  'Aire de lavage',
].map((label) => ({ label }))

/**
 * Les destinations constatées **avec leur gravité** : c'est elle qui décide de la phrase composée
 * (`composeDestinationProposals`) et de la conclusion dérivée. Une destination sans gravité serait
 * invisible de la règle.
 */
export const FALLBACK_DESTINATIONS: readonly DestinationChoice[] = [
  { label: 'Vers réseau EP', severity: 'bad' },
  { label: 'Vers fossé', severity: 'bad' },
  { label: 'Sur sol', severity: 'bad' },
  { label: 'Vers fosse', severity: 'bad' },
  { label: 'Vers puisard', severity: 'bad' },
  { label: 'Vers gargouille', severity: 'bad' },
  { label: 'Vers épandage', severity: 'bad' },
  { label: 'Vers milieu naturel', severity: 'bad' },
  { label: 'Non contrôlé', severity: 'warn' },
  { label: 'Non raccordé', severity: 'warn' },
  { label: 'Destination indéterminée', severity: 'warn' },
  { label: 'Réseau privé bouché', severity: 'bad' },
]

export const FALLBACK_TECHNICAL_ELEMENTS: readonly AnomalyChoice[] = [
  'Siphon cassé',
  'Siphon non étanche',
  'Absence de dégraisseur',
  'Absence de séparateur fécule',
  'WC broyeur',
  'BACKWASH',
  'Toutes les EU',
  'Non raccordé',
  'Étanchéité',
  'Regards EU non étanches',
  'REFUS',
  'Absence de séparateur hydrocarbures',
  'Raccordement possible vers réseau',
  'Présence de dégraisseur',
].map((label) => ({ label }))
